import type { PullRequestDetail } from "@afar-research/contracts";
import { PlusIcon, XIcon } from "lucide-react";
import { useState } from "react";

import { newCommandId } from "../../lib/utils";
import { executePullRequestDiscussion } from "../../pullRequestDiscussionCommand";
import { invalidatePullRequest } from "../../pullRequestInvalidation";
import type { PullRequestSurfaceDescriptor } from "../../pullRequestSurfaceStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { pullRequestLabelColor } from "./pullRequestPresentation";

/**
 * The labels a pull request carries, and the way to change them. Every change states the whole
 * set rather than a difference, which is what makes it safe to repeat: the Environment works out
 * what to add and remove from what it just read.
 */
export function PullRequestLabelEditor({
  descriptor,
  detail,
  enabled,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly detail: PullRequestDetail;
  readonly enabled: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const carried = detail.labels.map((label) => label.name);
  const canEdit =
    enabled &&
    detail.capabilities.labels === true &&
    detail.viewerPermissions.labels === true &&
    detail.projectRevision !== undefined;

  const setLabels = (labels: ReadonlyArray<string>) => {
    if (!canEdit || pending || detail.projectRevision === undefined) return;
    setPending(true);
    void executePullRequestDiscussion({
      environmentId: descriptor.environmentId,
      projectId: detail.projectId,
      repository: detail.repository,
      number: detail.number,
      expectedRevision: detail.projectRevision,
      resourceVersion: detail.resourceVersion,
      commandId: newCommandId(),
      action: { kind: "set-labels", labels },
    })
      .then((result) => {
        if (result.status === "settled" && result.receipt.status === "accepted") {
          invalidatePullRequest({
            environmentId: descriptor.environmentId,
            projectId: detail.projectId,
            repository: detail.repository,
            number: detail.number,
            partitions: ["summary", "list"],
          });
        }
      })
      .finally(() => setPending(false));
  };

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1">
      {detail.labels.length === 0 ? (
        <span className="text-muted-foreground">None</span>
      ) : (
        detail.labels.map((label) => {
          const dot = pullRequestLabelColor(label.color);
          return (
            <span
              className="inline-flex max-w-48 items-center gap-1.5 rounded-full bg-muted/40 py-0.5 pl-1.5 pr-1 text-xs"
              key={label.name}
            >
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full bg-muted-foreground"
                {...(dot === null ? {} : { style: { backgroundColor: dot } })}
              />
              <span className="truncate">{label.name}</span>
              {canEdit ? (
                <button
                  aria-label={`Remove label ${label.name}`}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  disabled={pending}
                  onClick={() => setLabels(carried.filter((name) => name !== label.name))}
                  type="button"
                >
                  <XIcon aria-hidden className="size-2.5" />
                </button>
              ) : (
                <span className="pr-1" />
              )}
            </span>
          );
        })
      )}
      {canEdit ? (
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setDraft("");
          }}
        >
          <PopoverTrigger
            render={
              <Button aria-label="Add a label" size="icon-xs" variant="ghost" disabled={pending} />
            }
          >
            <PlusIcon aria-hidden className="size-3.5" />
          </PopoverTrigger>
          <PopoverPopup align="start" className="w-64 p-2" side="bottom">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const name = draft.trim();
                // A label the pull request already carries is not a change to ask for.
                if (name.length === 0 || carried.includes(name)) return;
                setLabels([...carried, name]);
                setDraft("");
                setOpen(false);
              }}
            >
              <Input
                aria-label="Label to add"
                autoFocus
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Label name"
                value={draft}
              />
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                The label must already exist on the repository.
              </p>
            </form>
          </PopoverPopup>
        </Popover>
      ) : null}
    </span>
  );
}
